import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { mockData } from '../mock';
import axios from 'axios';
import { 
  Briefcase, 
  GraduationCap, 
  Users, 
  CheckCircle2, 
  ArrowRight, 
  TrendingUp,
  Target,
  Award,
  Clock,
  DollarSign,
  FileText
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Corporate = () => {
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const iconMap = {
    'briefcase': <Briefcase className="h-12 w-12" />,
    'graduation-cap': <GraduationCap className="h-12 w-12" />,
    'users': <Users className="h-12 w-12" />,
    'file-text': <FileText className="h-12 w-12" />
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      await axios.post(`${API}/corporate-inquiry`, formData);
      setSubmitStatus('success');
      setFormData({
        company_name: '',
        contact_name: '',
        email: '',
        phone: '',
        service: '',
        message: ''
      });
      
      // Reset success message after 5 seconds
      setTimeout(() => setSubmitStatus(null), 5000);
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 backdrop-blur-sm bg-white/95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <img src={mockData.logo} alt="JurisGo Logo" className="h-8 w-8 object-contain" />
              <span className="text-2xl font-semibold text-slate-900">JurisGo</span>
            </Link>
            <nav className="hidden md:flex space-x-8">
              <Link to="/" className="text-slate-700 hover:text-slate-900 transition-colors font-medium">Home</Link>
              <Link to="/trainings" className="text-slate-700 hover:text-slate-900 transition-colors font-medium">Trainings</Link>
              <Link to="/opportunities" className="text-slate-700 hover:text-slate-900 transition-colors font-medium">Opportunities</Link>
              <Link to="/blog" className="text-slate-700 hover:text-slate-900 transition-colors font-medium">Blog</Link>
              <Link to="/corporate" className="text-slate-900 font-semibold border-b-2 border-slate-900">Corporate</Link>
              <Link to="/wall-of-fame" className="text-slate-700 hover:text-slate-900 transition-colors font-medium">Wall of Fame</Link>
            </nav>
            <div className="flex items-center space-x-3">
              <Button asChild variant="outline" className="border-green-500 text-green-600 hover:bg-green-50 transition-colors">
                <a href={mockData.whatsappGroup} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-1">
                  <Users className="h-4 w-4" />
                  <span className="hidden lg:inline">Community</span>
                </a>
              </Button>
              <Button asChild className="bg-slate-900 hover:bg-slate-800 text-white transition-colors">
                <a href="#contact">Contact Us</a>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <Badge className="mb-6 bg-slate-700 text-white px-6 py-2 text-base">Enterprise Solutions</Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Transform Your Legal Operations
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 mb-8 leading-relaxed">
              Comprehensive paralegal solutions for law firms and corporate legal departments. 
              Scale efficiently, reduce costs, and enhance quality with JurisGo.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" asChild className="bg-white text-slate-900 hover:bg-slate-100 text-lg px-10 py-7">
                <a href="#services">Explore Services <ArrowRight className="ml-2 h-5 w-5" /></a>
              </Button>
              <Button size="lg" variant="outline" asChild className="border-slate-400 text-white hover:bg-slate-800 text-lg px-10 py-7">
                <a href="#contact">Schedule Consultation</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {mockData.corporateStats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl font-bold text-slate-900 mb-2">{stat.number}</div>
                <div className="text-lg text-slate-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-slate-100 text-slate-800 px-4 py-1">Our Services</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Tailored Solutions for Your Business
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Three powerful service offerings designed to meet the unique needs of law firms and corporate legal departments
            </p>
          </div>

          <div className="space-y-24">
            {mockData.corporateServices.map((service, index) => (
              <div key={service.id} className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 items-center`}>
                {/* Service Image/Icon Side */}
                <div className="flex-1">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-slate-100 to-slate-200 rounded-3xl transform rotate-3"></div>
                    <Card className="relative border-2 border-slate-200 shadow-xl bg-white">
                      <CardContent className="p-12 text-center">
                        <div className="inline-flex items-center justify-center w-24 h-24 bg-slate-900 text-white rounded-2xl mb-6">
                          {iconMap[service.icon]}
                        </div>
                        <h3 className="text-3xl font-bold text-slate-900 mb-3">{service.title}</h3>
                        <p className="text-lg text-slate-600">{service.subtitle}</p>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                {/* Service Details Side */}
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-slate-900 mb-4">{service.title}</h3>
                  <p className="text-lg text-slate-600 mb-6 leading-relaxed">{service.description}</p>
                  
                  <div className="mb-8">
                    <h4 className="text-xl font-semibold text-slate-900 mb-4 flex items-center">
                      <Target className="h-5 w-5 mr-2 text-slate-700" />
                      Key Benefits
                    </h4>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {service.benefits.map((benefit, idx) => (
                        <div key={idx} className="flex items-start space-x-2">
                          <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-slate-700">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mb-8">
                    <h4 className="text-xl font-semibold text-slate-900 mb-4 flex items-center">
                      <Award className="h-5 w-5 mr-2 text-slate-700" />
                      Service Offerings
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {service.services.map((item, idx) => (
                        <Badge key={idx} variant="outline" className="border-slate-300 text-slate-700 px-3 py-1">
                          {item}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <Button size="lg" className="bg-slate-900 hover:bg-slate-800 text-white">
                    Learn More <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Why Partner with JurisGo?</h2>
            <p className="text-xl text-slate-600">Industry-leading expertise meets innovative solutions</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <DollarSign className="h-8 w-8" />,
                title: "Cost Efficiency",
                description: "Reduce operational costs by up to 60% without compromising quality"
              },
              {
                icon: <TrendingUp className="h-8 w-8" />,
                title: "Scalability",
                description: "Flexible solutions that grow with your business needs"
              },
              {
                icon: <Clock className="h-8 w-8" />,
                title: "Quick Deployment",
                description: "Fast onboarding and implementation with minimal disruption"
              },
              {
                icon: <Award className="h-8 w-8" />,
                title: "Quality Assurance",
                description: "Certified professionals with proven track records"
              }
            ].map((item, index) => (
              <Card key={index} className="border-slate-200 hover:shadow-lg transition-shadow text-center">
                <CardContent className="p-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-900 text-white rounded-xl mb-4">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">{item.title}</h3>
                  <p className="text-slate-600">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact" className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Get in Touch</h2>
            <p className="text-xl text-slate-600 mb-4">
              Ready to transform your legal operations? Contact us for a consultation
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center text-slate-700">
              <a href="mailto:admin@jurisgo.in" className="flex items-center hover:text-slate-900 transition-colors">
                <span className="font-medium">Email: admin@jurisgo.in</span>
              </a>
              <span className="hidden sm:inline text-slate-400">|</span>
              <a href="tel:+918130588175" className="flex items-center hover:text-slate-900 transition-colors">
                <span className="font-medium">Phone: +91 8130588175</span>
              </a>
            </div>
          </div>

          <Card className="border-slate-200 shadow-xl">
            <CardContent className="p-8">
              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-2 text-green-800">
                  <CheckCircle2 className="h-5 w-5" />
                  <span>Thank you! Your inquiry has been submitted successfully. We&apos;ll contact you soon.</span>
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
                  <span>Sorry, there was an error submitting your inquiry. Please try again or contact us directly.</span>
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="company_name">Company Name *</Label>
                    <Input
                      id="company_name"
                      placeholder="Your Company"
                      value={formData.company_name}
                      onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_name">Contact Name *</Label>
                    <Input
                      id="contact_name"
                      placeholder="John Doe"
                      value={formData.contact_name}
                      onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="john@company.com"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="+91 XXXXX XXXXX"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="service">Service Interested In *</Label>
                  <select
                    id="service"
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900"
                    value={formData.service}
                    onChange={(e) => setFormData({...formData, service: e.target.value})}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Select a service</option>
                    <option value="lpo">Legal Process Outsourcing (Personal Injury)</option>
                    <option value="training">Corporate Training Programs (Personal Injury - Remote)</option>
                    <option value="placement">Placement & Hiring Solutions</option>
                    <option value="legal-drafting">Legal Drafting Services for Indian Lawyers</option>
                    <option value="all">All Services</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    placeholder="Tell us about your requirements..."
                    rows={5}
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    required
                    disabled={isSubmitting}
                  />
                </div>

                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white text-lg py-6"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Inquiry'} <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-800 text-slate-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img src={mockData.logo} alt="JurisGo Logo" className="h-6 w-6 object-contain" />
                <span className="text-xl font-semibold text-white">JurisGo</span>
              </div>
              <p className="text-slate-400">India&apos;s Premier International Paralegal Training Platform</p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Contact</h3>
              <p className="mb-2">Email: {mockData.footer.email}</p>
              <p className="mb-2">Phone: {mockData.footer.phone}</p>
              <p>Location: {mockData.footer.address}</p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li><Link to="/" className="hover:text-white transition-colors">Home</Link></li>
                <li><Link to="/trainings" className="hover:text-white transition-colors">Trainings</Link></li>
                <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
                <li><Link to="/corporate" className="hover:text-white transition-colors">Corporate</Link></li>
                <li><a href="/#faq" className="hover:text-white transition-colors">FAQ</a></li>
                <li><Link to="/admin" className="hover:text-white transition-colors">Admin</Link></li>
                <li><a href={mockData.whatsappGroup} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors flex items-center">
                  <Users className="h-4 w-4 mr-1" /> Join Community
                </a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-700 pt-8">
            <p className="text-sm text-slate-400 mb-4">{mockData.footer.disclaimer}</p>
            <p className="text-center text-slate-500">{mockData.footer.copyright}</p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Community Button */}
      <div className="fixed bottom-6 right-6 z-[10000]">
        <Button 
          asChild 
          className="bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all duration-300"
          size="lg"
        >
          <a 
            href={mockData.whatsappGroup} 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center space-x-2"
          >
            <Users className="h-6 w-6" />
            <span className="hidden sm:inline font-medium">Join Community</span>
          </a>
        </Button>
      </div>
    </div>
  );
};

export default Corporate;
